'use client';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useEffect, useState, type ElementType } from 'react';
import { CloudArrowUpIcon, CloudCheckIcon, CloudIcon, CloudSlashIcon, CloudWarningIcon } from '@phosphor-icons/react';
import {
  MediaSource,
  Slide,
  TimezoneDetails,
  Source,
  type FileSummary,
  type SourceContext,
} from '@/shared/types';
import {
  getFileInfoBatchFromServer,
  getFileInfoFromServer,
  extensionFromStoragePath,
  getMediaType,
  getTimeFromStr,
  getCanonicalContextPairFingerprint,
  getSourceMediaContextPairFingerprint,
  isArchiveMemberSearchablePath,
  isLikelyBase64MediaValue,
  toSearchablePath,
} from './functions';
import { extractPathAfterBulk } from './functions';
import { isDisplayableMediaType, PROVENANCE_STATUS_GROUPS } from '@/shared/constants';
import { ensureCitationSupInlineStyles } from '@/shared/citationSupBadge';
import { setSessionItem } from '@/lib/session-storage';
dayjs.extend(utc);
dayjs.extend(timezone);

export const isStatus = (
  status: string | undefined,
  group: keyof typeof PROVENANCE_STATUS_GROUPS,
): boolean => {
  if (!status || typeof status !== 'string') {
    return false;
  }

  const normalizedStatus = status.toLowerCase().trim();
  return (PROVENANCE_STATUS_GROUPS[group] ?? []).includes(normalizedStatus);
};

/** Same provenance UX as SUCCESS (icon, color, borders, tooltips). */
export const isProvenanceTrustedStatus = (status: string | undefined): boolean =>
  isStatus(status, 'SUCCESS') || isStatus(status, 'PARTIAL_SUCCESS');

/** True when ``status`` maps to a known provenance bucket (API value we style). */
export const isProvenanceKnownStatus = (status: string | undefined): boolean => {
  if (!status?.trim()) return false;
  return (
    isProvenanceTrustedStatus(status) ||
    isStatus(status, 'SKIPPED') ||
    isStatus(status, 'ERROR') ||
    isStatus(status, 'INTERMEDIARY')
  );
};

export const getStatusColor = (status: string | undefined): string => {
  if (!status || typeof status !== 'string') {
    return 'text-black/80';
  }

  if (isProvenanceTrustedStatus(status)) return 'text-green-400';
  if (isStatus(status, 'SKIPPED')) return 'text-gray-600';
  if (isStatus(status, 'ERROR')) return 'text-red-600';
  if (isStatus(status, 'INTERMEDIARY')) return 'text-orange-500';

  return 'text-black/80';
};

export const getStatusDescription = (status: string | undefined): string => {
  if (!status || typeof status !== 'string') {
    return 'Unknown verification status';
  }

  if (isProvenanceTrustedStatus(status)) return 'Verified and trusted source';
  if (isStatus(status, 'SKIPPED')) return 'Skipped - unsupported file type';
  if (isStatus(status, 'ERROR')) return 'Source verification failed or rejected';
  if (isStatus(status, 'INTERMEDIARY')) return 'Source verification in progress';

  return 'Unknown verification status';
};

export const formatProvenanceStatusLabel = (
  status: string | undefined,
  unknownLabel = 'Unknown',
): string => {
  const rawLabel = status?.trim() ? status : unknownLabel;
  const statusLabel = isProvenanceTrustedStatus(status)
    ? (PROVENANCE_STATUS_GROUPS.SUCCESS[0] ?? rawLabel)
    : rawLabel;
  return `Status: ${statusLabel} - ${getStatusDescription(status)}`;
};

export const getStatusIcon = (status: string | undefined): ElementType | null => {
  if (!status || typeof status !== 'string') {
    return null;
  }

  if (!isProvenanceKnownStatus(status)) {
    return null;
  }

  if (isProvenanceTrustedStatus(status)) return CloudCheckIcon;
  if (isStatus(status, 'SKIPPED')) return CloudSlashIcon;
  if (isStatus(status, 'ERROR')) return CloudWarningIcon;
  if (isStatus(status, 'INTERMEDIARY')) return CloudArrowUpIcon;

  return null;
};

const DEFAULT_PROVENANCE_BORDER = 'border-2 border-gray-300 rounded-md';

/** Icon size where ERROR and trusted states use a slightly larger glyph. */
export const getProvenanceIconDisplaySize = (status: string | undefined): number =>
  isStatus(status, 'ERROR') || isProvenanceTrustedStatus(status) ? 22 : 20;

/**
 * Colored ring around provenance icons. When `defaultWhenUnmatched` is true,
 * unknown non-empty statuses get a neutral gray ring (evidence locker toolbar).
 */
export const getProvenanceIconBorderClassNames = (
  status: string | undefined,
  options?: { defaultWhenUnmatched?: boolean },
): string => {
  if (isProvenanceTrustedStatus(status)) return 'border-2 border-green-300 rounded-md';
  if (isStatus(status, 'SKIPPED')) return 'border-2 border-gray-500 rounded-md';
  if (isStatus(status, 'ERROR')) return 'border-2 border-red-300 rounded-md';
  if (isStatus(status, 'INTERMEDIARY')) return 'border-2 border-orange-300 rounded-md';
  if (
    options?.defaultWhenUnmatched &&
    (!status?.trim() || !isProvenanceKnownStatus(status))
  ) {
    return DEFAULT_PROVENANCE_BORDER;
  }
  return '';
};

/** Panel-style frame (subtle shadow + ring) used in unified source lists. */
export const getProvenancePanelIconFrameClassNames = (status: string | undefined): string => {
  const parts: string[] = [];
  if (status?.trim()) {
    parts.push('drop-shadow-sm border border-current/20 rounded-sm');
  }
  if (isProvenanceTrustedStatus(status)) {
    parts.push('drop-shadow-lg');
  }
  const ring = getProvenanceIconBorderClassNames(status, { defaultWhenUnmatched: true });
  if (ring) parts.push(ring);
  return parts.join(' ');
};

/** Fixed canvas + text color + toolbar border ring (e.g. question-bar tooltips). */
export const getProvenanceToolbarIconClassNames = (
  status: string | undefined,
  options?: { withBorder?: boolean },
): string =>
  [
    'box-border h-[20px] w-[20px] min-w-[20px] min-h-[20px] max-h-[20px] max-w-[20px]',
    getStatusColor(status),
    options?.withBorder === false
      ? ''
      : getProvenanceIconBorderClassNames(status, { defaultWhenUnmatched: true }),
  ]
    .filter(Boolean)
    .join(' ');

/** Sessions cache the canonical provenance ``FileSummary`` for 15 minutes. */
export const FILE_INFO_TTL_MS = 900000;

/**
 * Short cache window (1 minute) used for audio/video/image file-info that is not
 * yet displayable: the provenance ``previewUrl`` is still missing or the file's
 * ``status`` hasn't reached ``COMPLETED``. Caching such a partial summary for the
 * full TTL would pin the UI to a preview-less (or still-processing) state for 15
 * minutes; the short window lets a follow-up render pick up the preview once
 * processing finishes.
 */
export const FILE_INFO_SHORT_TTL_MS = 60000;

/** File-info statuses for which we trust a fully-populated preview-bearing summary. */
const isFileInfoCompletedStatus = (status: string | undefined): boolean =>
  (status ?? '').trim().toUpperCase() === 'COMPLETED';

/**
 * Audio/video/image previews are produced asynchronously by provenance, so a
 * summary missing its ``previewUrl`` (or not yet ``COMPLETED``) should be cached
 * only briefly. Everything else uses the standard 15-minute TTL.
 */
const fileInfoCacheTtlMs = (
  sourceFilePath: string,
  fileInfo: FileSummary,
): number => {
  const mediaType = getMediaType(
    {
      sourceFilePath,
      sourceUrl: sourceFilePath,
      mediaUrl: sourceFilePath,
      mediaSrc: sourceFilePath,
    } as Source,
    fileInfo,
  );
  const isPreviewMediaType =
    mediaType === 'audio' || mediaType === 'video' || mediaType === 'image';
  if (!isPreviewMediaType) {
    return FILE_INFO_TTL_MS;
  }
  const hasPreviewUrl =
    typeof fileInfo.previewUrl === 'string' && fileInfo.previewUrl.trim() !== '';
  if (!hasPreviewUrl || !isFileInfoCompletedStatus(fileInfo.status)) {
    return FILE_INFO_SHORT_TTL_MS;
  }
  return FILE_INFO_TTL_MS;
};

const FILE_INFO_KEY_PREFIX = 'FI_';

const fileInfoCacheKey = (sourceFilePath: string, bypassCm = false): string =>
  `${FILE_INFO_KEY_PREFIX}${bypassCm ? 'bypass_' : ''}${toSearchablePath(sourceFilePath) || sourceFilePath}`;

/** Fresh cached summary, or ``undefined`` when the key is missing or expired. */
const readCachedFileInfo = (cacheKey: string): FileSummary | undefined => {
  try {
    const cachedRaw = sessionStorage.getItem(cacheKey);
    if (!cachedRaw) return undefined;
    const cached = JSON.parse(cachedRaw) as CachedFileInfo;
    if (cached?.value && typeof cached.exp === 'number' && cached.exp > Date.now()) {
      return cached.value;
    }
    sessionStorage.removeItem(cacheKey);
  } catch {
    try {
      sessionStorage.removeItem(cacheKey);
    } catch {
      // sessionStorage may be unavailable
    }
  }
  return undefined;
};

const rememberFileInfo = (
  sourceFilePath: string,
  fileInfo: FileSummary,
  bypassCm = false,
): void => {
  const cacheKey = fileInfoCacheKey(sourceFilePath, bypassCm);
  const entry: CachedFileInfo = {
    value: fileInfo,
    exp: Date.now() + fileInfoCacheTtlMs(sourceFilePath, fileInfo),
  };
  setSessionItem(sessionStorage, cacheKey, JSON.stringify(entry), 'cache');
};

const LEGACY_SOURCE_INFO_KEY_PREFIX = 'SI_';
const LEGACY_SOURCE_INFO_PURGE_SENTINEL = 'FI_PURGED_LEGACY';

/** Shape stored under a single ``FI_<url>`` key: the summary plus its absolute expiry. */
type CachedFileInfo = { value: FileSummary; exp: number };

/**
 * In-flight ``/files/file-info`` requests keyed by ``FI_<url>``. Two consumers
 * of the same file (e.g. a SourceCard and its embedded MediaDisplay) usually
 * mount in the same tick and both miss the sessionStorage cache before either
 * write lands, so without coalescing they each hit the network. Sharing the
 * promise collapses them into a single round-trip; the entry is cleared once it
 * settles so later lookups go through the persisted cache.
 */
const inFlightFileInfo = new Map<string, Promise<FileSummary | null>>();

/**
 * Drop the previous ``SI_*`` sessionStorage entries on first call after the
 * SourceInfo → FileSummary collapse so we don't carry the legacy shape's
 * fields around for free in every tab's quota. Guarded by a one-shot
 * sentinel; the sweep is O(n) on session keys but only runs once per tab.
 */
function purgeLegacySourceInfoCacheOnce() {
  try {
    if (typeof sessionStorage === 'undefined') return;
    if (sessionStorage.getItem(LEGACY_SOURCE_INFO_PURGE_SENTINEL) === '1') return;
    const stale: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith(LEGACY_SOURCE_INFO_KEY_PREFIX)) stale.push(key);
    }
    for (const key of stale) sessionStorage.removeItem(key);
    sessionStorage.setItem(LEGACY_SOURCE_INFO_PURGE_SENTINEL, '1');
  } catch {
    // sessionStorage may be unavailable (SSR, locked-down browser); silently skip.
  }
}

/**
 * Memoised ``FileSummary`` lookup for **one** file — used by sourceCard /
 * mediaDisplay / unifiedSources / media player to avoid repeating provenance
 * ``GET /files/file-info`` round-trips for every render.
 *
 * Do not call this in a loop, ``Promise.all``, or ``forEach`` over a list of
 * files. A list uses {@link getCurrentFileInfoBatch} (one
 * ``POST /files/file-info``). This GET stays for a single file, and for
 * ``bypass_cm`` (the batch route ignores that flag).
 *
 * Callers should pass the full ``s3://<bucket>/<key>`` URL when they have one
 * (e.g. via ``resolveFullS3Url``): provenance indexes by full URL, so a bare
 * relative key can miss. The cache/in-flight key is normalized to the canonical
 * ``searchablePath`` (via {@link toSearchablePath}) so the same file requested
 * as ``s3://<bucket>/data/bulk/<key>``, ``data/bulk/<key>``, or ``<key>`` shares
 * one entry — collapsing the partial-vs-full duplicate into a single round-trip.
 * The cached ``{ value, exp }`` pair lives under that key so the summary and its
 * expiry stay together.
 */
export const getCurrentFileInfo = async (
  sourceFilePath: string,
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
  bypassCm?: boolean,
): Promise<FileSummary | null> => {
  try {
    purgeLegacySourceInfoCacheOnce();
    const cacheKey = fileInfoCacheKey(sourceFilePath, bypassCm);
    const cached = readCachedFileInfo(cacheKey);
    if (cached) return cached;
    const existing = inFlightFileInfo.get(cacheKey);
    if (existing) {
      return await existing;
    }
    const request = (async () => {
      const fileInfo = await getFileInfoFromServer(sourceFilePath, cellId, accessToken, bypassCm);
      if (fileInfo) {
        // These entries expire lazily, on a re-read of the same key, so a
        // session that opens many files fills the quota with dead ones. The
        // safe write sweeps those before retrying; a still-full store just
        // means this lookup is not cached, which is non-fatal — and as a cache
        // write it never evicts a live peer to make room for itself.
        rememberFileInfo(sourceFilePath, fileInfo, bypassCm);
      }
      return fileInfo;
    })();
    inFlightFileInfo.set(cacheKey, request);
    try {
      return await request;
    } finally {
      inFlightFileInfo.delete(cacheKey);
    }
  } catch (error) {
    return null;
  }
}

/**
 * Load file-info for a list of paths with one provenance batch POST.
 *
 * Keep this as the list path. Do not replace a caller with
 * ``paths.map(getCurrentFileInfo)`` or one ``GET /files/file-info`` per file.
 * Chat citations and the content-moderation scan both go through here.
 *
 * Cache hits and already-in-flight single lookups are reused. Paths that miss
 * go out together. When the batch route is absent (``null``), each miss falls
 * back to {@link getCurrentFileInfo} so cells without the POST still resolve.
 * That per-file GET is only the fallback — do not make it the primary path.
 * ``bypass_cm`` stays on the single-file GET: the batch route ignores it.
 */
export const getCurrentFileInfoBatch = async (
  sourceFilePaths: string[],
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
): Promise<Map<string, FileSummary | null>> => {
  purgeLegacySourceInfoCacheOnce();
  const result = new Map<string, FileSummary | null>();
  const missing: string[] = [];
  const seen = new Set<string>();
  const inflight: Array<Promise<void>> = [];

  for (const path of sourceFilePaths) {
    const trimmed = path.trim();
    if (!trimmed || seen.has(trimmed)) continue;
    seen.add(trimmed);
    const cacheKey = fileInfoCacheKey(trimmed);
    const cached = readCachedFileInfo(cacheKey);
    if (cached) {
      result.set(trimmed, cached);
      continue;
    }
    const existing = inFlightFileInfo.get(cacheKey);
    if (existing) {
      inflight.push(
        existing.then((info) => {
          result.set(trimmed, info);
        }),
      );
      continue;
    }
    missing.push(trimmed);
  }

  if (inflight.length > 0) await Promise.all(inflight);
  if (missing.length === 0 || !cellId || !accessToken) {
    for (const path of missing) result.set(path, null);
    return result;
  }

  const batched = await getFileInfoBatchFromServer(missing, cellId, accessToken);
  // Per-file GET only when the batch route is absent (``null``). Do not
  // delete the POST above and leave this loop as the way a list is loaded.
  if (!batched) {
    await Promise.all(
      missing.map(async (path) => {
        result.set(path, await getCurrentFileInfo(path, cellId, accessToken));
      }),
    );
    return result;
  }

  for (const path of missing) {
    const info = batched.get(path) ?? null;
    if (info) rememberFileInfo(path, info);
    result.set(path, info);
  }
  return result;
};

/** Drop cached ``/files/file-info`` rows so a newly created finding is visible. */
export function invalidateFileInfoCache(sourceFilePath: string): void {
  const suffix = toSearchablePath(sourceFilePath) || sourceFilePath;
  if (!suffix) return;
  const keys = [`${FILE_INFO_KEY_PREFIX}${suffix}`, `${FILE_INFO_KEY_PREFIX}bypass_${suffix}`];
  for (const key of keys) {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // sessionStorage may be unavailable
    }
    inFlightFileInfo.delete(key);
  }
}


export const entityEventsToSlides = (eventsToParse: any[], sources: Source[], allSourcesFromChat: any) => {
  return eventsToParse.map((event: any, index: number) => {
    let sourcesForEvent: MediaSource[] = [];
    if (event.sources) {
      const sourcesFromEntityService = sources.map((source: Source) => {
        return {
          sourceType: extractPathAfterBulk(source.sourceFilePath || '').sourceType || 'Unknown',
          sourceUrl: source.sourceFilePath,
        }
      });
      sourcesForEvent = dedupeSources(sourcesFromEntityService);
    }

    // get the last 5 sources from allSourcesFromChat
    const numberOfExtraSourcesToInclude = 5;
    const extraSources = allSourcesFromChat?.allSources?.slice(0, numberOfExtraSourcesToInclude) || [];
    // get the last mediaSource from allSourcesFromChat
    const numberOfExtraMediaSourcesToInclude = 1;
    const extraMediaSources = allSourcesFromChat?.mediaSources?.slice(0,numberOfExtraMediaSourcesToInclude) || [];

    const sourcesToInclude = [ ...(sourcesForEvent || []), ...(extraSources || [])].slice(0, 5);
    // An empty entity ``media`` list stays empty so chat citations can be
    // attached later. Mixing in a chat extra here would look like the entity
    // job already supplied media and block that fill.
    const entityMedia = Array.isArray(event.media) ? event.media : [];
    const mediaSourcesToInclude = entityMedia.length > 0
      ? [...entityMedia, ...(extraMediaSources || [])]
      : [];
    const description = event.rawTexts.join('\n');
    const newEvent: Slide = {
      show: true,
      approval: 'approved',
      slideTitle: event.eventSummary,
      slideDescription: description,
      date: event.eventDate,
      media: mediaSourcesToInclude,
      sources: sourcesToInclude,
      location: event.location,
      eventLocalTimezone: event.eventLocalTimezone,
    }
    return newEvent;
  });
}

const chatMediaIdentityKeys = (item: MediaSource): Set<string> => {
  const keys = new Set<string>();
  const add = (value: unknown) => {
    if (typeof value !== 'string') return;
    const trimmed = value.trim();
    if (!trimmed) return;
    keys.add(trimmed);
    keys.add(trimmed.toLowerCase());
    const searchable = toSearchablePath(trimmed);
    if (searchable) {
      keys.add(searchable);
      keys.add(searchable.toLowerCase());
    }
  };
  add(item.sourceFilePath);
  add(item.sourceUrl);
  add(item.originalUrl);
  add(item.mediaUrl);
  add(item.mediaSrc);
  add(item.s3Url);
  add(item.previewFilePath);
  add(item.dispatchedFilePath);
  add(item.fileInfo?.fileUrl);
  add(item.fileInfo?.previewUrl);
  add(item.fileInfo?.searchablePath);
  add(item.mediaContext?.sourceUrl);
  add(item.mediaContext?.sourceFilePath);
  add(item.mediaContext?.previewUrl);
  return keys;
};

/**
 * Extensions that map to video or audio but are not reliably playable in the
 * browser. Same set as ``NON_NATIVE_HTML5_*`` in ``functions.ts``, plus MPEG-TS
 * (``.ts``), which provenance transcodes to ``converted_video.mp4``.
 *
 * A citation snapshotted at ``CREATE_JOB`` can still name one of these. The
 * job then runs long enough for file-info to return a transcode. Treating the
 * snapshot as playable would write it back over that transcode.
 */
const NON_BROWSER_PLAYABLE_AV_EXTENSIONS = new Set([
  'avi', 'mkv', 'flv', 'wmv', 'mpeg', 'mpg', 'dav', 'ts',
  'amr', 'wma', 'slk',
]);

const isBrowserPlayableAvUrl = (url: string): boolean => {
  const extension = extensionFromStoragePath(url);
  if (NON_BROWSER_PLAYABLE_AV_EXTENSIONS.has(extension)) return false;
  const mediaType = getMediaType({ mediaUrl: url, sourceFilePath: url });
  // Extensionless UFDR extracts are ``unknown``. A CREATE_JOB snapshot often
  // still names that original, and file-info may have already supplied a
  // transcode. ``unknown`` is not a playback URL.
  return mediaType === 'video' || mediaType === 'audio';
};

/**
 * Playback URL the chat source card already resolved for this citation.
 *
 * ``processCitation`` writes that URL onto ``mediaUrl`` / ``mediaSrc``. The
 * legacy wand sends only ``sourceUrl`` (the cited object, often the original
 * ``.avi`` / ``.ts`` or the container) to entity extraction, and that path is
 * what comes back on ``event.media``. Timeline-service persists ``mediaUrl``,
 * so the player then streams the unplayable object.
 *
 * A raw ``.avi`` / ``.ts``, an extensionless original, or a document conversion
 * / image still is not that playback URL. File-info may already have replaced
 * it with a transcode by the time this runs, and a document or image row must
 * keep the cited file.
 */
const chatPlayableMediaUrl = (source: MediaSource): string => {
  const playable = (source.mediaUrl || source.mediaSrc || '').trim();
  if (!playable || isArchiveMemberSearchablePath(playable) || !isBrowserPlayableAvUrl(playable)) {
    return '';
  }
  return playable;
};

/**
 * Media row for a chat citation, using the URL the source card already shows.
 * Entity extraction often returns events with no ``media``; these rows are what
 * get attached. Images and documents keep the cited file. Video and audio are
 * attached only when that URL is already browser-playable — an ``.avi``,
 * ``.ts``, or extensionless original is not written onto ``mediaUrl`` /
 * ``s3Url``, which is what the timeline player streams.
 */
const chatCitationMediaRow = (source: MediaSource): MediaSource | null => {
  const candidates = [source.mediaUrl, source.mediaSrc]
    .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
    .map((value) => value.trim())
    .filter((value) => !isArchiveMemberSearchablePath(value));
  const url = candidates[0];
  if (!url) return null;
  const fromUrl = getMediaType({ mediaUrl: url, sourceFilePath: url });
  const declared = (source.mediaType || '').toLowerCase();
  const mediaType = isDisplayableMediaType(fromUrl)
    ? fromUrl
    : isDisplayableMediaType(declared)
      ? declared
      : '';
  if (!mediaType) return null;
  const isAv = mediaType === 'video' || mediaType === 'audio'
    || fromUrl === 'video' || fromUrl === 'audio';
  const playbackUrl = isAv ? candidates.find((value) => isBrowserPlayableAvUrl(value)) : url;
  if (!playbackUrl) return null;
  const canonical = (source.sourceFilePath || source.sourceUrl || '').trim();
  return {
    mediaType,
    mediaUrl: playbackUrl,
    mediaSrc: playbackUrl,
    s3Url: playbackUrl,
    sourceUrl: source.sourceUrl || source.sourceFilePath,
    sourceFilePath: source.sourceFilePath || source.sourceUrl,
    fileInfo: source.fileInfo,
    content: source.content,
    mediaContext: {
      ...(source.mediaContext ?? source.sourceContext ?? {}),
      ...(canonical && playbackUrl !== canonical ? { previewUrl: playbackUrl } : {}),
    },
  };
};

const sharesChatMediaIdentity = (left: MediaSource, right: MediaSource): boolean => {
  const rightKeys = chatMediaIdentityKeys(right);
  for (const key of chatMediaIdentityKeys(left)) {
    if (rightKeys.has(key)) return true;
  }
  return false;
};

/**
 * Point legacy-wand video and audio rows at the playable URL from the chat answer.
 *
 * Matching is by any shared identity path (cited object, dispatched extract,
 * or an already-resolved transcode). ``s3Url`` is updated with ``mediaUrl``
 * because ``mapSlideToTimelineEventPayload`` persists ``s3Url`` first.
 * Document and image rows that already name a file are left alone, matching
 * ``applyPlayableAvPreview``. Chat citations are attached only when the
 * entity event has no media of its own.
 */
export function applyChatPlayableMediaToSlides(
  slides: Slide[],
  chatSources: MediaSource[] | null | undefined,
): Slide[] {
  const indexed = (chatSources ?? [])
    .map((source) => ({
      source,
      keys: chatMediaIdentityKeys(source),
      playable: chatPlayableMediaUrl(source),
    }))
    .filter((row) => row.playable !== '');
  const citationRows = (chatSources ?? [])
    .map((source) => chatCitationMediaRow(source))
    .filter((row): row is MediaSource => row !== null);
  if (indexed.length === 0 && citationRows.length === 0) return slides;

  return slides.map((slide) => {
    let changed = false;
    const media = (slide.media ?? []).map((item) => {
      const itemKeys = chatMediaIdentityKeys(item);
      let best: (typeof indexed)[number] | undefined;
      let bestScore = 0;
      for (const candidate of indexed) {
        let score = 0;
        for (const key of itemKeys) {
          if (candidate.keys.has(key)) score += key.length;
        }
        if (score > bestScore) {
          best = candidate;
          bestScore = score;
        }
      }
      if (!best) return item;
      const rowType = getMediaType(item, item.fileInfo);
      if (rowType !== 'video' && rowType !== 'audio' && rowType !== 'unknown') {
        return item;
      }
      const playable = best.playable;
      const s3Url = (item.s3Url || '').trim();
      if (
        (item.mediaUrl || '').trim() === playable &&
        (item.mediaSrc || '').trim() === playable &&
        (s3Url === '' || s3Url === playable)
      ) {
        return item;
      }
      changed = true;
      const canonical = (
        item.sourceUrl ||
        item.sourceFilePath ||
        best.source.sourceFilePath ||
        best.source.sourceUrl ||
        ''
      ).trim();
      const storedType = (best.source.mediaType || '').toLowerCase();
      const mediaType =
        storedType && storedType !== 'unknown' ? best.source.mediaType : item.mediaType;
      return {
        ...item,
        mediaType,
        mediaUrl: playable,
        mediaSrc: playable,
        s3Url: playable,
        sourceUrl: item.sourceUrl || best.source.sourceUrl || best.source.sourceFilePath,
        sourceFilePath: item.sourceFilePath || best.source.sourceFilePath,
        fileInfo: item.fileInfo ?? best.source.fileInfo,
        mediaContext: {
          ...(item.mediaContext ?? {}),
          ...(playable !== canonical ? { previewUrl: playable } : {}),
        },
      };
    });
    if (media.length > 0) {
      return changed ? { ...slide, media } : slide;
    }
    const attached: MediaSource[] = [];
    for (const row of citationRows) {
      if (attached.some((item) => sharesChatMediaIdentity(item, row))) continue;
      attached.push(row);
    }
    if (attached.length === 0) {
      return slide;
    }
    return { ...slide, media: attached };
  });
}

export const AUTOGENERATED_TIMELINE_PREFIX = 'AUTOGENERATED_TIMELINE:';
export const AUTOGENERATED_TIMELINE_SUFFIX = '</p><p>';

export function isAutogeneratedTimelineAnswer(answerText: string | null | undefined): boolean {
  if (typeof answerText !== 'string') {
    return false;
  }
  const events = parseAutogeneratedTimelineEvents(answerText);
  return events !== null && events.length > 0;
}

/** Extract a top-level JSON array literal starting at ``startIndex`` (must be ``[``). */
function extractJsonArrayLiteral(text: string, startIndex: number): string | null {
  if (text[startIndex] !== '[') {
    return null;
  }
  let depth = 0;
  let inString = false;
  let escaped = false;
  for (let i = startIndex; i < text.length; i++) {
    const ch = text[i];
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }
    if (ch === '"') {
      inString = true;
      continue;
    }
    if (ch === '[') {
      depth++;
    } else if (ch === ']') {
      depth--;
      if (depth === 0) {
        return text.slice(startIndex, i + 1);
      }
    }
  }
  return null;
}

/**
 * Parse the events from any answer containing the ``AUTOGENERATED_TIMELINE:`` marker
 * followed by a JSON array of objects. Anything before the marker, anything between
 * the marker and the opening ``[`` (whitespace, HTML tags, stray text), and anything
 * after the array's matching ``]`` is ignored — only the balanced JSON array is parsed.
 */
export function parseAutogeneratedTimelineEvents(answerText: string): unknown[] | null {
  const prefixIndex = answerText.indexOf(AUTOGENERATED_TIMELINE_PREFIX);
  if (prefixIndex === -1) {
    return null;
  }
  const searchStart = prefixIndex + AUTOGENERATED_TIMELINE_PREFIX.length;
  // Locate the start of the JSON array after the marker, skipping any characters
  // (whitespace, HTML tags, stray text) between the marker and the opening '['.
  const arrayStart = answerText.indexOf('[', searchStart);
  if (arrayStart === -1) {
    return null;
  }
  const jsonPart = extractJsonArrayLiteral(answerText, arrayStart);
  if (!jsonPart) {
    return null;
  }
  try {
    const parsed = JSON.parse(jsonPart);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Coerce ``mediaContext.page`` (assistant autogen payload) to ``pageNumber`` for timeline UI/API. */
export function normalizeMediaContextPageToPageNumber(
  mediaContext: SourceContext | null | undefined,
): SourceContext | undefined {
  if (!mediaContext || typeof mediaContext !== 'object') {
    return undefined;
  }
  const ctx = mediaContext as Record<string, unknown>;
  const pageRef = ctx.page;
  const hasPage = pageRef !== undefined && pageRef !== null && pageRef !== '';
  if (!hasPage) {
    if (ctx.pageNumber === undefined || ctx.pageNumber === null || ctx.pageNumber === '') {
      return mediaContext;
    }
    const existing =
      typeof ctx.pageNumber === 'number' ? ctx.pageNumber : parseInt(String(ctx.pageNumber), 10);
    if (!Number.isFinite(existing) || existing < 1) {
      return mediaContext;
    }
    return existing === ctx.pageNumber ? mediaContext : { ...ctx, pageNumber: existing };
  }
  const parsed =
    typeof pageRef === 'number' ? pageRef : parseInt(String(pageRef), 10);
  const pageNumber = Number.isFinite(parsed) && parsed >= 1 ? parsed : undefined;
  const { page: _page, ...rest } = ctx;
  if (pageNumber === undefined) {
    return Object.keys(rest).length > 0 ? (rest as SourceContext) : undefined;
  }
  return { ...rest, pageNumber } as SourceContext;
}

const normalizeAutogeneratedMediaItem = (item: MediaSource): MediaSource => {
  if (!item.mediaContext) {
    return item;
  }
  const mediaContext = normalizeMediaContextPageToPageNumber(item.mediaContext);
  if (mediaContext === item.mediaContext) {
    return item;
  }
  return { ...item, mediaContext };
};

/** Maps assistant ``AUTOGENERATED_TIMELINE`` JSON events to timeline ``Slide`` rows. */
export function autogeneratedTimelineEventsToSlides(events: unknown[]): Slide[] {
  return events.map((raw, index) => {
    const event = raw as Record<string, unknown>;
    const eventDateTime = typeof event.eventDateTime === 'string' ? event.eventDateTime : undefined;
    const eventLocalTimezone =
      typeof event.eventLocalTimezone === 'string' ? event.eventLocalTimezone : null;
    const { displayDate, time, localizedDateTime } = getDisplayDateAndTime(
      eventDateTime ?? '',
      eventLocalTimezone,
    );
    const displayOrder =
      typeof event.displayOrder === 'number' ? event.displayOrder : index;
    const sources = Array.isArray(event.sources)
      ? dedupeSources(event.sources as MediaSource[])
      : [];
    const media = Array.isArray(event.media)
      ? dedupeMediaSources((event.media as MediaSource[]).map(normalizeAutogeneratedMediaItem))
      : [];
    return {
      show: true,
      approval: 'approved',
      order: displayOrder,
      displayOrder,
      id: `NEW_AUTOGEN_${index}`,
      date: eventDateTime,
      displayDate,
      time,
      isTimeValid: event.isTimeValid === true,
      localizedDateTime,
      eventLocalTimezone: eventLocalTimezone ?? undefined,
      slideTitle: typeof event.slideTitle === 'string' ? event.slideTitle : undefined,
      slideDescription:
        typeof event.slideDescription === 'string' ? event.slideDescription : undefined,
      sources,
      media,
    };
  });
}

const AUTOGEN_URL_FIELDS: (keyof MediaSource)[] = [
  'sourceUrl',
  'mediaUrl',
  'mediaSrc',
  's3Url',
  'sourceFilePath',
  'originalUrl',
];

const autogenUrlCandidates = (item: MediaSource): string[] =>
  AUTOGEN_URL_FIELDS.map((field) => item[field])
    .filter((value): value is string => typeof value === 'string' && value.trim() !== '')
    .map((value) => value.trim());

/**
 * Upgrade a partial storage path (or non-``s3://`` URL) to a full ``s3://`` URI via
 * provenance ``/files/file-info`` (``fileUrl`` on the response).
 */
export async function resolveStorageUrlToFullS3(
  url: string | null | undefined,
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
): Promise<string | undefined> {
  const trimmed = (url ?? '').trim();
  if (!trimmed) {
    return undefined;
  }
  if (trimmed.startsWith('s3://')) {
    return trimmed;
  }
  if (isLikelyBase64MediaValue(trimmed)) {
    return trimmed;
  }
  const fileInfo = await getCurrentFileInfo(trimmed, cellId, accessToken);
  if (fileInfo?.fileUrl?.startsWith('s3://')) {
    return fileInfo.fileUrl;
  }
  return trimmed;
}

/**
 * Point a video/audio row at provenance's playable preview without moving the
 * canonical source path onto that URL.
 *
 * Legacy wand generation stores the cited object (often the original ``.avi``)
 * on ``mediaUrl``. The timeline builder used when ``better-wand`` is on stores
 * the transcode instead, which is what the presentation player streams.
 * ``mapSlideToTimelineEventPayload`` persists ``s3Url`` ahead of ``mediaUrl``,
 * so a row that still has the original on ``s3Url`` would save that original
 * even after ``mediaUrl`` moved.
 */
export function applyPlayableAvPreview(
  item: MediaSource,
  previewUrl: string | null | undefined,
  fileInfo?: FileSummary | null,
): MediaSource {
  const preview = (previewUrl ?? '').trim();
  if (!preview || isArchiveMemberSearchablePath(preview)) {
    return item;
  }
  const mediaType = getMediaType(item, fileInfo);
  if (mediaType !== 'video' && mediaType !== 'audio') {
    return item;
  }
  const playback = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
  const mediaUrl = playback(item.mediaUrl);
  const mediaSrc = playback(item.mediaSrc);
  const s3Url = playback(item.s3Url);
  // ``mapSlideToTimelineEventPayload`` persists ``s3Url`` ahead of ``mediaUrl``.
  // A row whose ``mediaUrl`` is already the transcode can still carry the
  // original on ``s3Url``; leaving that field alone saves the unplayable object.
  const playbackAlreadyPreview =
    (mediaUrl === preview || mediaUrl === '') &&
    (mediaSrc === preview || mediaSrc === '') &&
    (s3Url === preview || s3Url === '') &&
    (mediaUrl === preview || mediaSrc === preview || s3Url === preview);
  if (playbackAlreadyPreview) {
    return item;
  }
  const storedType = (item.mediaType || '').toLowerCase();
  return {
    ...item,
    mediaType: storedType === 'video' || storedType === 'audio' ? item.mediaType : mediaType,
    mediaUrl: preview,
    mediaSrc: preview,
    s3Url: preview,
    mediaContext: {
      ...(item.mediaContext ?? {}),
      previewUrl: preview,
    },
  };
}

const needsAutogenFileInfoUpgrade = (value: string): boolean => {
  const trimmed = value.trim();
  return trimmed !== '' && !trimmed.startsWith('s3://') && !isLikelyBase64MediaValue(trimmed);
};

/** URL ``resolveAutogenMediaItem`` asks file-info about after field upgrades. */
const canonicalAutogenLookup = (item: MediaSource): string =>
  (typeof item.sourceUrl === 'string' && item.sourceUrl.trim()) ||
  (typeof item.sourceFilePath === 'string' && item.sourceFilePath.trim()) ||
  playableUrl(item);

const upgradedStorageUrl = (
  url: string,
  infoByUrl: Map<string, FileSummary | null>,
): string => {
  const trimmed = url.trim();
  if (!trimmed || trimmed.startsWith('s3://') || isLikelyBase64MediaValue(trimmed)) {
    return trimmed;
  }
  const fileUrl = infoByUrl.get(trimmed)?.fileUrl;
  return fileUrl?.startsWith('s3://') ? fileUrl : trimmed;
};

const upgradeAutogenUrlFieldsFromMap = (
  item: MediaSource,
  infoByUrl: Map<string, FileSummary | null>,
): MediaSource => {
  if (autogenUrlCandidates(item).length === 0) {
    return item;
  }
  const next: MediaSource = { ...item };
  for (const field of AUTOGEN_URL_FIELDS) {
    const value = item[field];
    if (typeof value !== 'string' || value.trim() === '') {
      continue;
    }
    const upgraded = upgradedStorageUrl(value, infoByUrl);
    if (upgraded) {
      (next as Record<string, string | undefined>)[field] = upgraded;
    }
  }
  return next;
};

const upgradeAutogenUrlFields = async (
  item: MediaSource,
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
): Promise<MediaSource> => {
  if (autogenUrlCandidates(item).length === 0) {
    return item;
  }
  const infoByUrl = new Map<string, FileSummary | null>();
  for (const field of AUTOGEN_URL_FIELDS) {
    const value = item[field];
    if (typeof value !== 'string' || !needsAutogenFileInfoUpgrade(value)) {
      continue;
    }
    const trimmed = value.trim();
    if (infoByUrl.has(trimmed)) continue;
    infoByUrl.set(trimmed, await getCurrentFileInfo(trimmed, cellId, accessToken));
  }
  return upgradeAutogenUrlFieldsFromMap(item, infoByUrl);
};

const playableUrl = (item: MediaSource): string =>
  [item.mediaUrl, item.mediaSrc, item.s3Url].find(
    (value) => typeof value === 'string' && value.trim() !== '',
  )?.trim() ?? '';

/**
 * Apply a resolved file-info summary to one media row.
 *
 * Video and audio prefer provenance's preview transcode. A row with no
 * playable URL yet falls back to its canonical ``s3://`` path so the player
 * still has something to open when file-info has no preview.
 */
const finishAutogenMediaItem = (
  item: MediaSource,
  info: FileSummary | null,
): MediaSource => {
  const mediaType = getMediaType(item, info);
  if (mediaType === 'video' || mediaType === 'audio') {
    const withPreview = applyPlayableAvPreview(item, info?.previewUrl, info);
    if (withPreview !== item) {
      return withPreview;
    }
  }
  if (playableUrl(item)) {
    return item;
  }
  const canonical =
    (typeof item.sourceUrl === 'string' && item.sourceUrl.trim()) ||
    (typeof item.sourceFilePath === 'string' && item.sourceFilePath.trim()) ||
    (typeof item.originalUrl === 'string' && item.originalUrl.trim()) ||
    '';
  if (!canonical.startsWith('s3://')) {
    return item;
  }
  return { ...item, mediaUrl: canonical, mediaSrc: canonical };
};

/**
 * Summary already fetched for ``lookup``, including one stored under the
 * relative path that upgraded to this ``s3://`` URL. ``undefined`` means the
 * URL has not been asked about yet.
 */
const summaryForAutogenLookup = (
  lookup: string,
  infoByUrl: Map<string, FileSummary | null>,
): FileSummary | null | undefined => {
  if (infoByUrl.has(lookup)) return infoByUrl.get(lookup) ?? null;
  for (const summary of infoByUrl.values()) {
    if (summary?.fileUrl === lookup) return summary;
  }
  return undefined;
};

/**
 * Give a media row a URL the player can stream.
 *
 * Each populated field is upgraded on its own. A canonical ``sourceUrl`` that
 * is already ``s3://`` must not replace a different playable ``mediaUrl``
 * (TR-3842, legacy wand). Video and audio then prefer provenance's preview
 * transcode, leaving the cited object on ``sourceUrl``.
 */
const resolveAutogenMediaItem = async (
  item: MediaSource,
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
): Promise<MediaSource> => {
  const next = await upgradeAutogenUrlFields(item, cellId, accessToken);
  const lookup = canonicalAutogenLookup(next);
  // Type from file-info, not the path. Extensionless extracts (UFDR members)
  // have no suffix, so ``getMediaType`` without the summary returns ``unknown``
  // and the preview is never applied.
  const info = lookup ? await getCurrentFileInfo(lookup, cellId, accessToken) : null;
  return finishAutogenMediaItem(next, info);
};

const resolveAutogenMediaItemFromMap = (
  item: MediaSource,
  infoByUrl: Map<string, FileSummary | null>,
): MediaSource => {
  const next = upgradeAutogenUrlFieldsFromMap(item, infoByUrl);
  const lookup = canonicalAutogenLookup(next);
  const info = lookup ? (summaryForAutogenLookup(lookup, infoByUrl) ?? null) : null;
  return finishAutogenMediaItem(next, info);
};

/** Every storage path this pass would otherwise look up one request at a time. */
const collectAutogenFileInfoUrls = (slides: Slide[]): string[] => {
  const urls = new Set<string>();
  const addUpgradeFields = (item: MediaSource) => {
    for (const field of AUTOGEN_URL_FIELDS) {
      const value = item[field];
      if (typeof value === 'string' && needsAutogenFileInfoUpgrade(value)) {
        urls.add(value.trim());
      }
    }
  };
  for (const slide of slides) {
    for (const source of slide.sources ?? []) addUpgradeFields(source);
    for (const entry of slide.media ?? []) {
      addUpgradeFields(entry);
      const lookup = canonicalAutogenLookup(entry);
      if (lookup) urls.add(lookup);
    }
  }
  return [...urls];
};

const applyAutogenFileInfo = (
  slides: Slide[],
  infoByUrl: Map<string, FileSummary | null>,
): Slide[] =>
  slides.map((slide) => ({
    ...slide,
    sources: slide.sources?.map((source) => upgradeAutogenUrlFieldsFromMap(source, infoByUrl)),
    media: slide.media?.map((entry) => resolveAutogenMediaItemFromMap(entry, infoByUrl)),
  }));

const resolveAutogeneratedTimelineSlidesPerUrl = (
  slides: Slide[],
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
): Promise<Slide[]> =>
  Promise.all(
    slides.map(async (slide) => {
      const sources = slide.sources
        ? await Promise.all(
            slide.sources.map((source) => upgradeAutogenUrlFields(source, cellId, accessToken)),
          )
        : slide.sources;
      const media = slide.media
        ? await Promise.all(
            slide.media.map((entry) => resolveAutogenMediaItem(entry, cellId, accessToken)),
          )
        : slide.media;
      return { ...slide, sources, media };
    }),
  );

/**
 * Lookups that appear only after a relative path is rewritten to ``fileUrl``.
 * The summary that produced the rewrite is reused when it is the same object.
 */
const collectUnresolvedAutogenLookups = (
  slides: Slide[],
  infoByUrl: Map<string, FileSummary | null>,
): string[] => {
  const missing = new Set<string>();
  for (const slide of slides) {
    for (const entry of slide.media ?? []) {
      const lookup = canonicalAutogenLookup(upgradeAutogenUrlFieldsFromMap(entry, infoByUrl));
      if (lookup && summaryForAutogenLookup(lookup, infoByUrl) === undefined) {
        missing.add(lookup);
      }
    }
  }
  return [...missing];
};

/**
 * Resolve every source/media URL on autogenerated timeline slides before POST/PATCH.
 *
 * One ``POST /files/file-info`` for the slide list (and one follow-up POST for
 * paths that appear only after a rewrite). Do not replace either call with a
 * per-URL ``getCurrentFileInfo`` / ``GET /files/file-info``. The per-URL path
 * runs only when {@link getFileInfoBatchFromServer} returns ``null``.
 */
export async function resolveAutogeneratedTimelineSlides(
  slides: Slide[],
  cellId: string | null | undefined,
  accessToken: string | null | undefined,
): Promise<Slide[]> {
  const urls = collectAutogenFileInfoUrls(slides);
  if (!cellId || !accessToken || urls.length === 0) {
    return applyAutogenFileInfo(slides, new Map());
  }

  const batched = await getFileInfoBatchFromServer(urls, cellId, accessToken);
  if (!batched) {
    return resolveAutogeneratedTimelineSlidesPerUrl(slides, cellId, accessToken);
  }

  const infoByUrl = new Map(batched);
  const unresolved = collectUnresolvedAutogenLookups(slides, infoByUrl);
  if (unresolved.length > 0) {
    const followUp = await getFileInfoBatchFromServer(unresolved, cellId, accessToken);
    if (followUp) {
      for (const [url, summary] of followUp) infoByUrl.set(url, summary);
    } else {
      for (const url of unresolved) {
        infoByUrl.set(url, await getCurrentFileInfo(url, cellId, accessToken));
      }
    }
  }

  return applyAutogenFileInfo(slides, infoByUrl);
}

export function useWindowSize() {
  const [windowSize, setWindowSize] = useState<{ width: number | undefined, height: number | undefined }>({
    width: undefined,
    height: undefined,
  });

  useEffect(() => {
    let raf = 0;
    function handleResize() {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        setWindowSize({
          width: window.innerWidth,
          height: window.innerHeight,
        });
      });
    }
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => {
      window.removeEventListener("resize", handleResize);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);
  return windowSize;
}

export const getDescriptionFromAnswerText = (answerText: string) => {
  // Convert block-level HTML tags to markdown-friendly line breaks and strip
  // their wrappers, while leaving inline elements (e.g. color spans) intact.
  // Citation <sup> chips get inline badge styles so event/title editors and
  // paste targets keep the grey pill without relying on Tailwind classes.
  return ensureCitationSupInlineStyles(
    answerText
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/?(?:p|div|section|article|header|footer|main|nav|aside)(?:\s[^>]*)?>/gi, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .trim(),
  );
}

export const getTitleAndDescriptionFromAnswerText = (answerText: string) => {
  const firstLine = answerText.split('\n')[0];
  const title = firstLine.replace(/^#\s*/, '');
  const description = answerText.split('\n').
    slice(1).join('\n').
    replace(/##\s*/, '');
  return { title, description };
}

/** True when trimmed citation content is parseable JSON object/array text. */
const contentLooksLikeJson = (content: string): boolean => {
  const full = content.trim();
  if (
    !(
      (full.startsWith('{') && full.endsWith('}')) ||
      (full.startsWith('[') && full.endsWith(']'))
    )
  ) {
    return false;
  }
  try {
    JSON.parse(full);
    return true;
  } catch {
    return false;
  }
};

export const dedupeSources = (sourceObjects: MediaSource[]) => {
  if(!sourceObjects || sourceObjects.length === 0) return [];

  const seen = new Map<string, MediaSource>();

  sourceObjects.forEach(source => {
    // JSON citations often share a canonical path (and sometimes the same
    // resolved mediaUrl) while carrying distinct inline excerpts — include
    // content in the key so they stay aligned with getSourceIdentityKey /
    // buildEventMediaFromSources / event media.
    const content = (source.content || '').trim();
    const contentKey =
      content &&
      (source.mediaType === 'json' ||
        source.sourceType === 'json' ||
        contentLooksLikeJson(content))
        ? content.slice(0, 96)
        : '';
    const key = JSON.stringify({
      sourceUrl: source.sourceUrl,
      sourceType: source.sourceType,
      contentKey: contentKey || undefined,
      contextFingerprint: getCanonicalContextPairFingerprint(source),
    });
    // Only add if we haven't seen this exact combination
    if (!seen.has(key)) {
      seen.set(key, source);
    }
  });

  return Array.from(seen.values());
}

/** Stable React list key for media rows — mirrors dedupe identity fields plus context. */
export function getMediaSourceListRowKey(media: MediaSource, index = 0): string {
  const mediaUrl = (media.mediaUrl || media.mediaSrc || '').trim();
  const sourceUrl = (media.sourceUrl || '').trim();
  const sourceFilePath = (media.sourceFilePath || '').trim();
  const originalUrl = (media.originalUrl || '').trim();
  const identity = mediaUrl || sourceUrl || sourceFilePath || originalUrl;

  const content = (media.content || '').trim();
  const contentKey =
    media.mediaType === 'json' && content ? content.slice(0, 96) : '';
  const sourceIdentity =
    media.mediaType === 'json'
      ? (media.sourceUrl || media.sourceFilePath || '').trim()
      : '';

  return JSON.stringify({
    identity: identity || `media-row-${index}`,
    mediaType: media.mediaType,
    contentKey: contentKey || undefined,
    sourceIdentity: sourceIdentity || undefined,
    contextFingerprint: getCanonicalContextPairFingerprint(media),
  });
}

export const dedupeMediaSources = (mediaSources: MediaSource[]) => {
  if(!mediaSources || mediaSources.length === 0) return [];

  const seen = new Map<string, MediaSource>();

  mediaSources.forEach(media => {
    const mediaUrl = media.mediaUrl || media.mediaSrc || '';
    const content = (media.content || '').trim();
    // Sibling JSON excerpts often share one dispatched file URL — include
    // content whenever present so they stay aligned with dedupeSources.
    const contentKey =
      media.mediaType === 'json' && content ? content.slice(0, 96) : '';
    const sourceIdentity =
      media.mediaType === 'json'
        ? (media.sourceUrl || media.sourceFilePath || '').trim()
        : '';
    const key = JSON.stringify({
      mediaUrl,
      mediaType: media.mediaType,
      contentKey: contentKey || undefined,
      sourceIdentity: sourceIdentity || undefined,
      contextFingerprint: getCanonicalContextPairFingerprint(media),
    });

    // Only add if we haven't seen this exact combination
    if (!seen.has(key)) {
      seen.set(key, media);
    }
  });

  return Array.from(seen.values());
}

export function bytesToSize(usageInBytes: number) {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  if (usageInBytes === 0) return 'n/a';
  const i = Math.floor(Math.log(usageInBytes) / Math.log(1024));
  if (i === 0) return `${usageInBytes} ${sizes[i]})`;
  return `${(usageInBytes / (1024 ** i)).toFixed(1)} ${sizes[i]}`;
}

// Parse times into hours and minutes
export const parseTime = (timeStr: string) => {
  const [time, modifier] = timeStr.split(/\s+/);
  let [hours, minutes] = time.split(':').map(Number);
  if (modifier) {
    if (modifier.toLowerCase() === 'pm' && hours !== 12) hours += 12;
    if (modifier.toLowerCase() === 'am' && hours === 12) hours = 0;
  }
  return hours * 60 + (minutes || 0);
};

export const formatTime: (value: number, concise?: boolean, hoursOnly?: boolean) => string = (value: number, concise = false, hoursOnly = false) => {
  const MINUTES_IN_HOUR = 60;
  const MINUTES_IN_DAY = MINUTES_IN_HOUR * 24;
  const MINUTES_IN_WEEK = MINUTES_IN_DAY * 7;
  const MINUTES_IN_MONTH = MINUTES_IN_DAY * 30; // Approximation
  const MINUTES_IN_YEAR = MINUTES_IN_DAY * 365; // Approximation

  const years = Math.floor(value / MINUTES_IN_YEAR);
  const months = Math.floor((value % MINUTES_IN_YEAR) / MINUTES_IN_MONTH);
  const weeks = Math.floor((value % MINUTES_IN_MONTH) / MINUTES_IN_WEEK);
  const days = Math.floor((value % MINUTES_IN_WEEK) / MINUTES_IN_DAY);
  const hours = Math.floor((value % MINUTES_IN_DAY) / MINUTES_IN_HOUR);
  const minutes = Math.ceil(value % MINUTES_IN_HOUR);

  if (hoursOnly) {
    const totalHours = Math.floor(value / MINUTES_IN_HOUR);
    return `${totalHours.toLocaleString()} hour${totalHours !== 1 ? 's' : ''}`;
  }

  const parts = [];
  if (years > 0) parts.push(`${years} year${years !== 1 ? 's' : ''}`);
  if (months > 0) parts.push(`${months} month${months !== 1 ? 's' : ''}`);
  if (weeks > 0) parts.push(`${weeks} week${weeks !== 1 ? 's' : ''}`);
  if (days > 0) parts.push(`${days} day${days !== 1 ? 's' : ''}`);
  if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (minutes > 0 || parts.length === 0) parts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);

  if (concise) {
    return `More than ${parts.slice(0, 2).join(' and ')}`;
  }

  if (parts.length > 1) {
    const lastPart = parts.pop();
    return `${parts.join(', ')}, and ${lastPart}`;
  }

  return parts[0];
};

export const getDisplayDateAndTime = (eventDateTime: string, eventLocalTimezone: string | null): { displayDate: string | undefined, time: string | undefined, localizedDateTime: string | undefined } => {
  let displayDate = undefined;
  let time = undefined;
  let localizedDateTime = undefined;

  if (eventDateTime && eventLocalTimezone) {
    localizedDateTime = dayjs(eventDateTime).tz(eventLocalTimezone).format('YYYY-MM-DD hh:mm:ss A');
//    localizedDateTime = dayjs(eventDateTime).utc().format('YYYY-MM-DD hh:mm:ss A');
  } else if (eventDateTime) {
    localizedDateTime = dayjs(eventDateTime).utc().format('YYYY-MM-DD hh:mm:ss A');
  }

  if (eventLocalTimezone) { // we have a timzone with the dateTime, so use it
    displayDate = eventDateTime ? dayjs(eventDateTime).tz(eventLocalTimezone).format('YYYY-MM-DD') : undefined;
    time = eventDateTime ? dayjs(eventDateTime).tz(eventLocalTimezone).format('hh:mm:ss A') : undefined;
  } else { // no timezone, so we probably want date and time in UTC without any offset
    displayDate = eventDateTime ? dayjs(eventDateTime).utc().format('YYYY-MM-DD') : undefined;
    time = eventDateTime ? dayjs(eventDateTime).utc().format('hh:mm:ss A') : undefined;
  }

  return { displayDate, time, localizedDateTime };
}

/** First non-empty tenant id; treats `''` and whitespace-only strings as missing (like `||`). */
export function resolveTenantId(
  ...candidates: (string | null | undefined)[]
): string | null {
  for (const candidate of candidates) {
    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (trimmed !== '') {
        return trimmed;
      }
    }
  }
  return null;
}

/** First non-empty Cognito login name (not email/sub UUID). */
export function resolveCognitoUsername(
  ...candidates: (string | null | undefined)[]
): string | null {
  return resolveTenantId(...candidates);
}

const COGNITO_SUB_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Legacy main.tsx authState stored login name in `claims.sub` instead of `cognito:username`. */
export function legacyCognitoLoginFromClaimsSub(
  sub: string | null | undefined,
): string | null {
  if (typeof sub !== 'string' || sub.trim() === '') return null;
  const trimmed = sub.trim();
  return COGNITO_SUB_UUID_RE.test(trimmed) ? null : trimmed;
}

export const stringifyLocation = (location: { coordinates: [number, number] | null; name: string | null; address: string | null; } | null | undefined) => {
  if (!location) return '--';
  if (location.address) return JSON.stringify(location.address);
  if (location.name) return JSON.stringify(location.name);
  if (location.coordinates) return JSON.stringify(location.coordinates);
  return '--';
}

export const sortByRealDatetime = (a: Slide, b: Slide, timezoneListByName: Record<string, TimezoneDetails>) => {
  const idMod = (a.id || '') < (b.id || '') ? -1 : 1;
  if (!a.date && !b.date) return idMod;
  if (!a.date) return -1;
  if (!b.date) return 1;
  const dataAOffset = timezoneListByName[a.eventLocalTimezone || '']?.offsetInMilliseconds;
  const dataBOffset = timezoneListByName[b.eventLocalTimezone || '']?.offsetInMilliseconds;
  const dateAMilliseconds = new Date(a.date).getTime();
  const dateBMilliseconds = new Date(b.date).getTime();
  const dateA = dateAMilliseconds - (dataAOffset || 0);
  const dateB = dateBMilliseconds - (dataBOffset || 0);

  if (dateA < dateB) return -1;
  if (dateA > dateB) return 1;
  return idMod;
}

export const sortTwoSlidesByDatetime = (a: Slide, b: Slide) => {
  const idMod = (a.id || '') < (b.id || '') ? -1 : 1;
  if (!a.date && !b.date) return idMod;
  if (!a.date) return -1;
  if (!b.date) return 1;

  const dateA = new Date(new Date(a.date).toDateString()).getTime();
  const dateB = new Date(new Date(b.date).toDateString()).getTime();

  return compareSlidesByDatetimeAfterDate(a, b, dateA, dateB, idMod);
};

const getUtcCalendarDayTimestamp = (dateIso: string): number | null => {
  const dateOnly = dateIso.split('T')[0];
  const parsed = dayjs.utc(dateOnly);
  if (parsed.isValid()) return parsed.startOf('day').valueOf();
  const fallback = dayjs.utc(dateIso);
  return fallback.isValid() ? fallback.startOf('day').valueOf() : null;
};

const compareSlidesByDatetimeAfterDate = (
  a: Slide,
  b: Slide,
  dateA: number,
  dateB: number,
  idMod: number,
): number => {
  if (dateA < dateB) return -1;
  if (dateA > dateB) return 1;

  if (!a.time && !b.time) return idMod;
  if (a.time === b.time) return idMod;
  if (!a.time) return -1;
  if (!b.time) return 1;

  const timeA = a.time.toLowerCase().trim();
  const timeB = b.time.toLowerCase().trim();

  const aTime = getTimeFromStr(timeA);
  const bTime = getTimeFromStr(timeB);
  const aNumber = aTime?.hoursNumber
    ? aTime.hoursNumber * 3600 + (aTime.minutesNumber || 0) * 60 + (aTime.secondsNumber || 0)
    : 0;
  const bNumber = bTime?.hoursNumber
    ? bTime.hoursNumber * 3600 + (bTime.minutesNumber || 0) * 60 + (bTime.secondsNumber || 0)
    : 0;
  if (aNumber < bNumber) return -1;
  if (aNumber > bNumber) return 1;
  return idMod;
};

/** Datetime sort using UTC calendar days — matches horizontal timeline axis labels. */
export const sortTwoSlidesByDatetimeUtc = (a: Slide, b: Slide): number => {
  const idMod = (a.id || '') < (b.id || '') ? -1 : 1;
  if (!a.date && !b.date) return idMod;
  if (!a.date) return -1;
  if (!b.date) return 1;

  const dateA = getUtcCalendarDayTimestamp(a.date);
  const dateB = getUtcCalendarDayTimestamp(b.date);
  if (dateA == null && dateB == null) return idMod;
  if (dateA == null) return -1;
  if (dateB == null) return 1;

  return compareSlidesByDatetimeAfterDate(a, b, dateA, dateB, idMod);
};

export const sortTwoSlidesByDisplayOrder = (a: Slide, b: Slide) => {
  if (!a.displayOrder) return -1;
  if (!b.displayOrder) return 1;
  return a.displayOrder - b.displayOrder;
}

/** True when the slide has a non-empty, parseable timeline date. */
export const hasTimelineDate = (slide: Pick<Slide, 'date'>): boolean => {
  const raw = slide.date?.trim();
  if (!raw) return false;
  return dayjs.utc(raw.split('T')[0]).isValid() || dayjs.utc(raw).isValid();
};

/** Format a slide/API date string as a calendar day in UTC (avoids local TZ drift). */
export const formatSlideDateUtc = (dateIso: string, format: string): string | null => {
  const dateOnly = dateIso.split('T')[0];
  const parsed = dayjs.utc(dateOnly);
  if (parsed.isValid()) return parsed.format(format);
  const fallback = dayjs.utc(dateIso);
  return fallback.isValid() ? fallback.format(format) : null;
};

/**
 * Horizontal timeline ordering: dated events chronologically first, then
 * undated events in display-order. Undated events must never be dropped.
 */
export const sortSlidesForHorizontalTimeline = (a: Slide, b: Slide): number => {
  const aHasDate = hasTimelineDate(a);
  const bHasDate = hasTimelineDate(b);
  if (aHasDate && bHasDate) return sortTwoSlidesByDatetimeUtc(a, b);
  if (aHasDate && !bHasDate) return -1;
  if (!aHasDate && bHasDate) return 1;
  return sortTwoSlidesByDisplayOrder(a, b);
};

export const getSortedSlides = (unsortedSlides: Slide[], sortBy: 'displayOrder' | 'datetime' = 'displayOrder'): Slide[] => {
  const sortedSlides = unsortedSlides.sort((a: Slide, b: Slide) => {
    if (sortBy === 'displayOrder') {
      return sortTwoSlidesByDisplayOrder(a, b);
    } else {
      return sortTwoSlidesByDatetime(a, b);
    }
  });

  let displayOrder = 0;
  for (const slide of sortedSlides) {
    displayOrder += 1;
    slide.displayOrder = displayOrder;
    slide.order = displayOrder;
    slide.show = slide.show !== undefined ? slide.show : true;
  }
  return sortedSlides;
}

const TIMELINE_TITLE_INCREMENTAL_SUFFIX_RE = /^(.+?) \((\d+)\)$/;

/**
 * Pick a timeline title that does not collide with ``existingTitles``. When the
 * proposed name is taken, appends `` (1)``, `` (2)``, … to the base name (stripping
 * an existing `` (n)`` suffix from the proposal first).
 */
export function resolveUniqueTimelineTitle(
  proposedTitle: string,
  existingTitles: readonly string[],
): string {
  const proposed = proposedTitle.trim();
  if (!proposed) {
    return proposed;
  }

  const existing = new Set(
    existingTitles.map((title) => title.trim()).filter((title) => title.length > 0),
  );
  if (!existing.has(proposed)) {
    return proposed;
  }

  const suffixMatch = proposed.match(TIMELINE_TITLE_INCREMENTAL_SUFFIX_RE);
  const base = (suffixMatch ? suffixMatch[1] : proposed).trim() || proposed;

  let n = 1;
  while (existing.has(`${base} (${n})`)) {
    n += 1;
  }
  return `${base} (${n})`;
}
